import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const shortId = (id) => String(id || "").slice(0, 8);

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API is unavailable on some mobile / non-secure contexts.
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  }
}

/** A crawl ID with a copy button. `short` shows the first 8 characters;
    the button always copies the full ID. */
export default function CrawlId({ id, short = false, className, testid }) {
  const [copied, setCopied] = useState(false);
  if (!id) return null;

  const onCopy = async (e) => {
    e.stopPropagation();
    if (await copyText(id)) {
      setCopied(true);
      toast.success(`Copied crawl ID ${shortId(id)}…`);
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error("Couldn't copy -- select the ID and copy it manually");
    }
  };

  return (
    <span className={cn("inline-flex items-center gap-1 min-w-0 font-mono", className)} data-testid={testid}>
      <span className={cn("select-all", short ? "shrink-0" : "break-all")} title={id}
        data-testid={testid ? `${testid}-text` : undefined}>
        {short ? shortId(id) : id}
      </span>
      <button type="button" onClick={onCopy} title="Copy full crawl ID" aria-label="Copy full crawl ID"
        data-testid={testid ? `${testid}-copy` : undefined}
        className="shrink-0 -m-1 p-1 rounded text-slate-500 hover:text-cyan-300 hover:bg-slate-800/60 transition-colors">
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
}
